import { Box, Grid, Stack, Typography } from '@mui/material';
import { ProgressItem } from 'pages/tokenomics/tokenomics/ui/progress-info/ui';
import { BORDER_MAIN, useBreakpoints } from 'shared';
import { PageNavigationMenu } from 'widgets';

import {
  allocationLeadText,
  allocationRows,
  characteristics,
  ecosystemItems,
  emissionRows,
  futureDevelopmentItems,
  getStartedItems,
  introActivities,
  menuItems,
  networkBenefitItems,
  overviewRows,
  securityItems,
  strategicImportanceItems,
  textSections,
  utilityExpansionItems,
  utilityItems,
} from '../constants';

import { BulletList } from './bullet-list';
import { ContentTable } from './content-table';
import { formatRichText } from './format-rich-text';
import { InfoSection } from './info-section';

type TokenCitContentProps = {
  activeSection: string;
};

const SectionDivider = () => <Box sx={{ borderTop: BORDER_MAIN }} />;

export const TokenCitContent = ({ activeSection }: TokenCitContentProps) => {
  const { isDownMdLayout } = useBreakpoints();

  return (
    <Grid container mb={{ xs: 5, md: 7 }}>
      <Grid item xs={12} md={4} display={{ xs: 'none', md: 'block' }}>
        <PageNavigationMenu activeSection={activeSection} items={menuItems} buttonLabel="Content navigation" />
      </Grid>

      <Grid item container xs={12} md={8}>
        <Stack spacing={{ xs: 4, md: 5 }}>
          {isDownMdLayout && (
            <PageNavigationMenu
              activeSection={activeSection}
              isMobile
              items={menuItems}
              buttonLabel="Content navigation"
            />
          )}

          <InfoSection id="introduction" title="Introduction">
            <Stack spacing={3}>
              <Typography variant="body2" className="primary" sx={{ whiteSpace: 'pre-line' }}>
                {formatRichText(textSections[0].paragraphs[0])}
              </Typography>
              <BulletList items={introActivities} />
              <Typography variant="body2" className="primary">
                {formatRichText(textSections[0].paragraphs[2])}
              </Typography>
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="role" title="Role of CIT in the CratD2C Ecosystem">
            <Stack spacing={3}>
              {textSections[1].paragraphs.map((paragraph) => (
                <Typography key={paragraph} variant="body2" className="primary">
                  {paragraph}
                </Typography>
              ))}
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="characteristics" title="Key Characteristics of CIT">
            <Stack spacing={2}>
              {characteristics.map(({ title, description }) => (
                <Box key={title}>
                  <Typography variant="h5" className="regular" sx={{ mb: 1, letterSpacing: '0.1px' }}>
                    {title}
                  </Typography>
                  {title === 'Ecosystem Utility Expansion' ? (
                    <Stack spacing={1}>
                      <Typography variant="body2" className="secondary">
                        CIT can be used for:
                      </Typography>
                      <BulletList items={utilityExpansionItems} />
                    </Stack>
                  ) : (
                    <Typography variant="body2" className="secondary">
                      {description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="ecosystem" title="CIT within the CratD2C 12+1 Ecosystem">
            <Typography variant="body2" className="secondary" sx={{ mb: 3 }}>
              {formatRichText(
                '<mark>CIT is integrated across the entire CratD2C ecosystem architecture</mark>, providing incentives and engagement rewards that encourage participation and accelerate ecosystem adoption.',
              )}
            </Typography>
            <Stack spacing={4}>
              {ecosystemItems.map(([title, description]) => (
                <Box key={title}>
                  <Typography variant="h5" className="regular" sx={{ mb: 1, letterSpacing: '0.1px' }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" className="secondary">
                    {formatRichText(description)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="overview" title="Tokenomics Overview">
            <ContentTable
              ariaLabel="Tokenomics overview"
              columns={[
                { key: 'parameter', label: 'Parameter', width: '50%' },
                { key: 'value', label: 'Value', width: '50%' },
              ]}
              rows={overviewRows}
            />
          </InfoSection>

          <SectionDivider />

          <InfoSection id="emission" title="CIT Supply Emission Model">
            <Stack spacing={3}>
              <Typography variant="body2" className="secondary">
                The CIT token follows the same supply discipline used by the $CRAT coin, known as the Biennial 8-Layer
                Zig-Zag Supply Mechanism.
              </Typography>
              <Typography variant="body2" className="secondary">
                This system releases tokens every two years, allowing the ecosystem to grow organically while preventing
                excessive inflation.
              </Typography>
            </Stack>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h4" className="large semi-bold" sx={{ mb: 3 }}>
                Biennial Supply Distribution
              </Typography>
              <ContentTable
                ariaLabel="Biennial supply distribution"
                columns={[
                  { key: 'layer', label: 'Layer', width: '25%' },
                  { key: 'period', label: 'Period', width: '25%' },
                  { key: 'supplyPercent', label: 'Supply %', width: '25%' },
                  { key: 'emission', label: 'CIT Emission', width: '25%' },
                ]}
                rows={emissionRows}
              />
              <Typography variant="h5" className="regular" sx={{ mt: 3 }}>
                Total Supply:{' '}
                <Typography component="span" variant="inherit" className="accent">
                  30,000,000,000 CIT
                </Typography>
              </Typography>
            </Box>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="allocation" title="Supply Allocation">
            <Stack spacing={3}>
              <Typography variant="body2" className="secondary">
                {allocationLeadText}
              </Typography>
              {allocationRows.map(({ title, amount, percent, description }) => (
                <ProgressItem
                  key={title}
                  title={title}
                  value={percent}
                  label={amount}
                  percentage={percent}
                  subtitle={description}
                />
              ))}
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="utility" title="CIT Utility">
            <Typography variant="body2" className="secondary" sx={{ mb: 3 }}>
              CIT unlocks several ecosystem functions:
            </Typography>
            <Stack spacing={2}>
              {utilityItems.map(([title, description]) => (
                <Box key={title}>
                  <Typography variant="h5" className="regular" sx={{ mb: 1, letterSpacing: '0.1px' }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" className="secondary">
                    {description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="security" title="Security & Infrastructure">
            <Stack spacing={1}>
              <Typography variant="body2" className="primary">
                CIT is built on the CratD2C D2C-20 token standard, which ensures:
              </Typography>
              <BulletList items={securityItems} />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="body2" className="primary">
                The network benefits from:
              </Typography>
              <BulletList items={networkBenefitItems} />
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="importance" title="Strategic Importance of CIT">
            <Stack spacing={1}>
              <Typography variant="body2" className="primary">
                CIT plays a critical role in accelerating user growth and engagement.
              </Typography>
              <Typography variant="body2" className="secondary">
                It ensures that:
              </Typography>
              <BulletList items={strategicImportanceItems} />
              <Typography variant="body2" className="primary">
                This approach transforms the CratD2C ecosystem into a self-reinforcing economic network, where
                participation directly contributes to network expansion.
              </Typography>
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="future" title="Future Development">
            <Stack spacing={1}>
              <Typography variant="body2" className="primary">
                The CIT ecosystem will continue evolving with the launch of:
              </Typography>
              <BulletList items={futureDevelopmentItems} />
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="conclusion" title="Conclusion">
            <Stack spacing={2}>
              {textSections[5].paragraphs.map((paragraph) => (
                <Typography key={paragraph} variant="body2" className="primary">
                  {paragraph}
                </Typography>
              ))}
            </Stack>
          </InfoSection>

          <SectionDivider />

          <InfoSection id="get-started" title="Get Started">
            <Stack spacing={4}>
              {getStartedItems.map(({ title, description }) => (
                <Box key={title}>
                  <Typography variant="h5" className="regular" sx={{ mb: description ? 1 : 0, letterSpacing: '0.1px' }}>
                    {title}
                  </Typography>
                  {description && (
                    <Typography variant="body2" className="secondary">
                      {description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </InfoSection>
        </Stack>
      </Grid>
    </Grid>
  );
};
